import "./styles.css";

import { Box, Button, HStack, IconButton, useMediaQuery } from "@chakra-ui/react";
import { CloseIcon, HamburgerIcon } from "@chakra-ui/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ReactElement, useMemo, useState } from "react";

import { AppRoutes } from "src/common/constants/constantEnums";
import { EnglishLabels } from "src/common/constants/englishLabels";
import { GlobalLangSelector } from "src/common/globalLangSelector";
import { HindiLabels } from "src/common/constants/hindiLabels";
import MksLogo from "src/resources/icons/mksLogo";
import { RootState } from "src/store/store";
import SideDrawer from "./sideDrawer";
import { useSelector } from "react-redux";
import UserAvatarMenu from "src/common/UserAvatarMenu";

export interface NavMenu {
    name: string;
    hindi: string;
    onClick: () => void;
}

interface NavbarProps {
    isScrolled: boolean;
    activeSection?: string;
}

const HomeNavbar: React.FC<NavbarProps> = (props: NavbarProps) => {
    const { isScrolled, activeSection } = props;

    const [sideDrawerOpen, setSideDrawerOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const [isMobile] = useMediaQuery("(max-width: 426px)");
    const [isTablet] = useMediaQuery("(min-width: 426px) and (max-width: 768px)");
    const [isSmallScreen] = useMediaQuery("(min-width: 768px) and (max-width: 1000px)");

    const globalLanguage = useSelector((state: RootState) => state.globalInfo.globalLanguage);
    const isHindiLabels = globalLanguage === "HI";

    const isDonationPage = location.pathname.includes("donation");

    const activeNavLabel = useMemo(() => {
        if (location.pathname.startsWith(AppRoutes.MATRIMONY_PROFILE_DETAILS)) {
            return EnglishLabels.Profile_Details;
        }
        if (location.pathname.startsWith(AppRoutes.MATRIMONY_CONTACT_REQUEST)) {
            return EnglishLabels.Contact_Requests;
        }
        if (location.pathname.startsWith(AppRoutes.MATRIMONY)) {
            return EnglishLabels.Home_Page;
        }
        return activeSection || "";
    }, [activeSection, location.pathname]);

    const handleNavigation = (label: string): void => {
        const navigationMap: Record<string, (() => void) | undefined> = {
            [EnglishLabels.Home_Page]: () => navigate(AppRoutes.MATRIMONY),
            [EnglishLabels.Contact_Requests]: () => navigate(AppRoutes.MATRIMONY_CONTACT_REQUEST),
            [EnglishLabels.Profile_Details]: () => navigate(AppRoutes.MATRIMONY_PROFILE_DETAILS),
        };

        const action = navigationMap[label];
        if (action) action();
    };

    const navButtons: NavMenu[] = [
        {
            name: EnglishLabels.Home_Page,
            hindi: HindiLabels.Home_Page,
            onClick: () => handleNavigation(EnglishLabels.Home_Page),
        },
        {
            name: EnglishLabels.Contact_Requests,
            hindi: HindiLabels.Contact_Requests,
            onClick: () => handleNavigation(EnglishLabels.Contact_Requests),
        },
        {
            name: EnglishLabels.Profile_Details,
            hindi: HindiLabels.Profile_Details,
            onClick: () => handleNavigation(EnglishLabels.Profile_Details),
        }
    ];

    const handleNavClick = (nav: NavMenu): void => {
        nav.onClick();
        if (sideDrawerOpen) setSideDrawerOpen(false);
    };

    const renderHeaderAvatar = (): ReactElement => {
        return (
            <Box>
                <UserAvatarMenu />
            </Box>
        );
    };

    const renderLangSelectorMenuRenderer = (): ReactElement => {
        return (
            <HStack
                px={2}
                alignItems="center"
                height="4rem"
                bg="white"
                borderRadius="10px"
                boxShadow={(isMobile || isTablet || isSmallScreen || isScrolled)
                    ? ""
                    : "0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 8px 16px 0px rgba(0, 0, 0, 0.08)"}
            >
                <GlobalLangSelector />
            </HStack>
        );
    };

    return (
        <HStack
            py={2}
            px={[0, 0, 0, 10]}
            transition="bg 0.3s ease"
            bg={(isMobile || isTablet || isSmallScreen || isScrolled) ? "white" : "transparent"}
            justifyContent="space-evenly"
            position="absolute"
            w="100%"
            boxShadow={(isMobile || isTablet || isSmallScreen || isScrolled) ? "0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 8px 16px 0px rgba(0, 0, 0, 0.08)" : ""}
        >
            <Box
                borderRadius="10px"
                bg="white"
                height="4rem"
                width="4rem"
                boxShadow={(isMobile || isTablet || isSmallScreen || isScrolled) ? "" : "0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 8px 16px 0px rgba(0, 0, 0, 0.08)"}
            >
                <Link to={AppRoutes.LANDING_PAGE}><MksLogo /></Link>
            </Box>

            <HStack
                hidden={isMobile || isTablet || isSmallScreen}
                w="55%"
                px={2}
                height="4rem"
                bg="white"
                borderRadius="10px"
                justifyContent="space-around"
                paddingX={10}
                boxShadow={(isMobile || isTablet || isSmallScreen || isScrolled) ? "" : "0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 8px 16px 0px rgba(0, 0, 0, 0.08)"}
            >
                {navButtons.map((nav) => {
                    const isActive = nav.name === activeNavLabel || (isDonationPage && nav.name === EnglishLabels.Donation);
                    return (
                        <Button
                            key={nav.name}
                            className="navbar-buttons"
                            backgroundColor={isActive ? "#4A2C2A" : "white"}
                            color={isActive ? "white" : "#4A2C2A"}
                            onClick={() => handleNavClick(nav)}
                            fontWeight={500}
                        >
                            {isHindiLabels ? nav.hindi : nav.name}
                        </Button>
                    );
                })}
            </HStack>

            {(isMobile || isTablet || isSmallScreen) ? (
                <HStack w="60%" justifyContent="flex-end" spacing={2}>
                    <IconButton
                        bgColor="transparent"
                        aria-label={sideDrawerOpen ? "Close menu" : "Open menu"}
                        icon={sideDrawerOpen ? <CloseIcon boxSize={5} /> : <HamburgerIcon boxSize={8} />}
                        onClick={() => setSideDrawerOpen((s) => !s)}
                    />
                    {renderLangSelectorMenuRenderer()}
                    {renderHeaderAvatar()}
                </HStack>
            ) : (
                <HStack spacing={2}>
                    {renderLangSelectorMenuRenderer()}
                    {renderHeaderAvatar()}
                </HStack>
            )}

            {(isMobile || isTablet || isSmallScreen) && sideDrawerOpen ? (
                <SideDrawer
                    sideDrawerOpen={sideDrawerOpen}
                    onClose={() => setSideDrawerOpen(false)}
                    menu={navButtons}
                    activeSection={activeNavLabel}
                    handleNavClick={(nav: NavMenu) => handleNavClick(nav)}
                />
            ) : null}
        </HStack>
    );
};

export default HomeNavbar;
